import { Fragment, useEffect, useState } from 'react';
import { Link, useMatch } from 'react-router-dom';

import styled from 'styled-components';

import { Breadcrumb } from '@components/breadcrumb';
import { Card } from '@components/card';
import { useDebouncedValue, useLocalStorageState } from '@components/component-helpers';
import { EmptyFiltersState } from '@components/empty-filters-state';
import { EmptySearchState } from '@components/empty-search-state';
import { EmptyState } from '@components/empty-state';
import { RouterTabs } from '@components/header/router-tabs';
import { IconButton } from '@components/icon-button';
import { IconActivity } from '@components/icons/activity';
import { IconArchive } from '@components/icons/archive';
import { IconJob } from '@components/icons/job';
import { IconPublish } from '@components/icons/publish';
import { IconSettings } from '@components/icons/settings';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { IconTime } from '@components/icons/time';
import { IconUnarchive } from '@components/icons/unarchive';
import { Pagination, usePagination } from '@components/pagination';
import { SearchBox } from '@components/search-box';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { abbreviateDate } from '@utils/date';

import {
  useArchive,
  useMarkAsRead,
  useMarkAsUnread,
  useNotificationCounts,
  useNotificationList,
  useUnArchive,
} from 'api/notifications/hooks';
import { Notification, NotificationSection } from 'api/notifications/requests';
import { Header } from 'components/headers';
import { Loading } from 'components/Loading';
import { PageContent } from 'components/Page';
import { Quickfilter } from 'components/Quickfilter';
import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';

import {
  getIconForAction,
  getIconForNotification,
  getUrlForNotification,
  NotificationContent,
} from './helpers';

export const Notifications = () => {
  const { t } = useStore();
  const [tabIndex, setTabIndex] = useLocalStorageState({
    key: 'notifications-tab',
    defaultValue: 0,
    valueAsNumber: true,
  });
  const section: NotificationSection =
    (useMatch('/notifications/:section/*')?.params.section as NotificationSection) ?? 'activity';
  const countsQuery = useNotificationCounts();
  const [_searchValue, setSearchValue] = useState('');
  const searchValue = useDebouncedValue(_searchValue, 300);
  return (
    <>
      <Header
        // @ts-ignore
        breadcrumbs={<Breadcrumb items={[{ label: t('notifications.title') }]} />}
        actions={
          <NotifcationActions
            setSearchValue={setSearchValue}
            section={section}
            latestId={countsQuery.data?.latestId}
          />
        }
        tabs={<NotificationTabs section={section} />}
      />
      {section === 'activity' && (
        <Quickfilter
          key={section}
          onChange={setTabIndex}
          defaultIndex={tabIndex}
          items={[
            {
              count: countsQuery.data?.total ?? 0,
              label: t('notifications.filters.all'),
              render: () => <NotificationList searchValue={searchValue} section={section} />,
            },
            {
              count: countsQuery.data?.unread ?? 0,
              label: t('notifications.filters.unread'),
              render: () => <NotificationList unread searchValue={searchValue} section={section} />,
            },
            {
              count: countsQuery.data?.newApplications ?? 0,
              label: t('notifications.filters.newapplications'),
              render: () => (
                <NotificationList newApplication searchValue={searchValue} section={section} />
              ),
            },
            {
              count: countsQuery.data?.assigned ?? 0,
              label: t('notifications.filters.assigned'),
              render: () => (
                <NotificationList assignedToMe searchValue={searchValue} section={section} />
              ),
            },
          ]}
        />
      )}
      {section === 'archive' && <NotificationList searchValue={searchValue} section={section} />}
    </>
  );
};

const NotifcationActions = ({
  latestId,
  section,
  setSearchValue,
}: {
  latestId?: number;
  section: NotificationSection;
  setSearchValue: (value: string) => void;
}) => {
  const { t } = useStore();
  const { showModal } = useModal();

  const markAllAsReadModal = () => {
    showModal('NOTIFICATIONS_MARK_ALL_AS_READ', { latestId: latestId ?? 0 });
  };
  const archiveAllModal = () => {
    showModal('NOTIFICATIONS_ARCHIVE_ALL', { latestId: latestId ?? 0 });
  };
  return (
    <Stack space={2} orientation="horizontal">
      {section === 'activity' && (
        <>
          <IconButton
            icon={<IconStatusIndicator color="green100" />}
            onClick={markAllAsReadModal}
            tooltip={t('notifications.title.markallasread')}
          />
          <IconButton
            icon={<IconArchive />}
            onClick={archiveAllModal}
            tooltip={t('notifications.title.archiveall')}
          />
        </>
      )}
      <SearchBox onChange={setSearchValue} placeholder={t('notifications.search.placeholder')} />
      <Link to="/profile/notifications/">
        <IconButton icon={<IconSettings />} tooltip={t('notifications.tooltips.settings')} />
      </Link>
    </Stack>
  );
};

const NotificationTabs = ({ section }: { section: string }) => {
  const { t } = useStore();

  return (
    <RouterTabs
      ariaLabel={t('notifications.tabs.label')}
      activeSection={section}
      tabs={[
        {
          label: t('notifications.tabs.activity'),
          to: '/notifications/activity',
          section: 'activity',
          icon: <IconActivity />,
        },
        {
          label: t('notifications.tabs.archive'),
          to: '/notifications/archive',
          section: 'archive',
          icon: <IconArchive />,
        },
      ]}
    />
  );
};

type NotificationGroup = {
  [key: string]: Notification[];
};

type NotificationListProps = {
  unread?: boolean;
  newApplication?: boolean;
  assignedToMe?: boolean;
  mentions?: boolean;
  searchValue?: string;
  section: NotificationSection;
};

export const NotificationList = (props: NotificationListProps) => {
  const { lang, t } = useStore();
  const [count, setCount] = useState(0);
  const pageSize = 50;
  const { numPages, selectedPage, selectPage } = usePagination({
    pageSize,
    defaultPage: 1,
    count,
  });
  const notificationsQuery = useNotificationList({
    ...props,
    pageSize,
    offset: (selectedPage - 1) * pageSize,
    setCount: setCount,
  });
  useEffect(() => {
    selectPage(1);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [props.searchValue]);
  if (notificationsQuery.isFetching || !notificationsQuery.data) {
    return <Loading />;
  }
  const groups = notificationsQuery.data.results.reduce(
    (groups: NotificationGroup, notification) => {
      const date: string = notification.created.split('T')[0];
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(notification);
      return groups;
    },
    {},
  );
  const hasSearch = Boolean(props.searchValue);
  const hasFilters = Boolean(props.unread || props.newApplication || props.assignedToMe);
  const isEmpty = notificationsQuery.data.results.length === 0;
  return (
    <PageContent key={selectedPage}>
      <Grid>
        <GridColumnMiddle space={2}>
          {isEmpty && hasSearch && <EmptySearchState hasFilters={hasFilters} />}
          {isEmpty && !hasSearch && hasFilters && <EmptyFiltersState />}
          {isEmpty && !hasSearch && !hasFilters && (
            <EmptyState
              icon={<IconPublish />}
              content={t('notifications.empty.content')}
              header={t('notifications.empty.header')}
            />
          )}
          {Object.keys(groups).map((date) => {
            return (
              <Fragment key={date}>
                <Text font="semibold14" text={abbreviateDate(date, lang, 'date-fancy')} />
                {groups[date].map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    notification={notification}
                    section={props.section}
                  />
                ))}
              </Fragment>
            );
          })}
          <Pagination
            justify="right"
            count={numPages}
            selected={selectedPage}
            onSelect={selectPage}
          />
        </GridColumnMiddle>
      </Grid>
    </PageContent>
  );
};

type NotificationProps = {
  notification: Notification;
  section: NotificationSection;
};

export const NotificationCard = (props: NotificationProps) => {
  const { notification } = props;
  const { lang, t, userStore } = useStore();
  const url = getUrlForNotification(notification);
  const content = (
    <NotificationContent
      afterText={notification.after?.name}
      applicationIds={notification.applicationIds}
      applicationName={notification.applicationName}
      beforeText={notification.before?.name}
      notificationType={notification.notificationType}
      objectId={notification.objectId}
      triggerType={notification.triggerType}
      userId={notification.user}
    />
  );

  const user = userStore.items.get(notification.user);

  const icon = getIconForNotification(notification.notificationType, user);
  const actionIcon = getIconForAction(notification.notificationType);
  const markAsRead = useMarkAsRead(notification.id);
  const markAsUnread = useMarkAsUnread(notification.id);
  const archive = useArchive(notification.id);
  const unArchive = useUnArchive(notification.id);

  return (
    <Link to={url} onClick={() => !notification.isRead && markAsRead.mutate()}>
      <Card spaceX={6} spaceY={2} interactive>
        <Stack space={2} orientation="horizontal" align="center" justify="space-between">
          <Stack space={6} orientation="horizontal" align="center">
            {icon}
            <Stack space={0}>
              <Stack space={2} orientation="horizontal" align="center">
                <IconJob size={16} color="purple50" />
                <Text font="semibold14" text={notification.jobTitle} />
              </Stack>
              <Stack space={2} orientation="horizontal" align="center">
                {actionIcon}
                <Text font="regular14" text={content} />
              </Stack>
              <Stack space={2} orientation="horizontal" align="center">
                <IconTime size={16} color="purple50" />
                <Text
                  font="regular14"
                  text={abbreviateDate(notification.created, lang, 'datetime')}
                  color="purple60"
                />
              </Stack>
            </Stack>
          </Stack>
          <Stack space={1} orientation="horizontal" align="center">
            {props.section === 'activity' && (
              <IconButton
                variant="ghost-purple"
                icon={
                  <IconStatusIndicator
                    color={markAsRead.isLoading || notification.isRead ? 'purple20' : 'green100'}
                  />
                }
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  notification.isRead ? markAsUnread.mutate() : markAsRead.mutate();
                }}
                tooltip={
                  notification.isRead
                    ? t('notifications.tooltips.markasunread')
                    : t('notifications.tooltips.markasread')
                }
              />
            )}
            <IconButton
              variant="ghost-purple"
              icon={notification.isArchived ? <IconUnarchive /> : <IconArchive />}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                notification.isArchived ? unArchive.mutate() : archive.mutate();
              }}
              tooltip={
                notification.isArchived
                  ? t('notifications.tooltips.unarchive')
                  : t('notifications.tooltips.archive')
              }
            />
          </Stack>
        </Stack>
      </Card>
    </Link>
  );
};

const Grid = styled.div({
  display: 'grid',
  '@media (min-width: 375px)': {
    gridTemplateColumns: 'repeat(4, 1fr)',
    gridColumnGap: '1em',
  },
  '@media (min-width: 600px)': {
    gridTemplateColumns: 'repeat(8, 1fr)',
    gridColumnGap: '1.5em',
  },
  '@media (min-width: 960px)': {
    gridTemplateColumns: 'repeat(8, 1fr)',
    gridColumnGap: '1.5em',
  },
  '@media (min-width: 1280px)': {
    gridTemplateColumns: 'repeat(12, 1fr)',
    gridColumnGap: '1.5em',
  },
});

const GridColumnMiddle = styled(Stack)({
  '@media (min-width: 375px)': {
    gridColumn: '1 / span 4',
  },
  '@media  (min-width: 600px)': {
    gridColumn: '2 /  span 6',
  },
  '@media (min-width: 960px)': {
    gridColumn: '2 / span 6',
  },
  '@media (min-width: 1280px)': {
    gridColumn: '3 / span 8',
  },
});
